/**
 * Heatmap.tsx — a 7×24 (weekday × hour) intensity grid for call-volume /
 * busiest-times (#22/#24, 2026-07-21). Counts only — never money. Cell shade
 * scales with count relative to the busiest cell; colours are CSS variables so
 * dark mode reskins for free. The server returns SPARSE buckets (non-zero
 * cells); this fills the full grid with zeros.
 */
import { Fragment } from 'react';
import type { ActivityBucket } from '../api/types';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Hour columns to label (kept sparse so the axis stays readable). */
const HOUR_LABELS = new Set([0, 6, 12, 18, 23]);

export function Heatmap({ buckets, ariaLabel }: { buckets: ActivityBucket[]; ariaLabel: string }) {
  // Fill a dense 7×24 grid from the sparse buckets.
  const grid: number[][] = DAYS.map(() => new Array(24).fill(0));
  let max = 0;
  for (const b of buckets) {
    if (b.dayOfWeek >= 0 && b.dayOfWeek < 7 && b.hour >= 0 && b.hour < 24) {
      grid[b.dayOfWeek][b.hour] = b.count;
      if (b.count > max) max = b.count;
    }
  }

  /** 0 → surface; >0 → primary at an alpha that scales with intensity. */
  const cellColor = (count: number): string => {
    if (count === 0 || max === 0) return 'var(--gps-surface-alt, var(--gps-surface))';
    const alpha = 0.15 + 0.85 * (count / max);
    return `color-mix(in srgb, var(--gps-primary) ${Math.round(alpha * 100)}%, transparent)`;
  };

  return (
    <figure style={{ margin: 0, overflowX: 'auto' }}>
      <div role="img" aria-label={ariaLabel} style={{ display: 'inline-grid', gridTemplateColumns: `auto repeat(24, 1fr)`, gap: '2px', minWidth: '520px' }}>
        {/* Header row: a blank corner + hour labels. */}
        <span aria-hidden="true" />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={`h-${h}`} aria-hidden="true" style={{ fontSize: '9px', textAlign: 'center', color: 'var(--gps-muted)' }}>
            {HOUR_LABELS.has(h) ? h : ''}
          </span>
        ))}
        {/* One row per weekday. */}
        {DAYS.map((day, d) => (
          <Fragment key={day}>
            <span style={{ fontSize: '11px', color: 'var(--gps-muted)', paddingRight: '6px', alignSelf: 'center' }}>
              {day}
            </span>
            {grid[d].map((count, h) => (
              <span
                key={`${day}-${h}`}
                title={`${day} ${String(h).padStart(2, '0')}:00 — ${count}`}
                style={{ aspectRatio: '1 / 1', minWidth: '12px', borderRadius: '2px', background: cellColor(count) }}
              />
            ))}
          </Fragment>
        ))}
      </div>
      <p style={{ fontSize: '12px', color: 'var(--gps-muted)', margin: 'var(--gps-s-2) 0 0' }}>
        Times shown in UK local time (Europe/London). Darker = busier{max > 0 ? ` (peak ${max})` : ''}.
      </p>
    </figure>
  );
}
